import { flow } from '@helo/utils'
import { LinePath } from '@visx/shape'
import { useId } from 'react'

import { type ChartColorPalette, Colors } from '../../Colors'
import { Options } from '../../Options'
import type { QueryOutput } from '../../Query'
import { RenderPipeline } from '../../RenderPipeline'
import type {
  ChartDataOutput,
  ChartDataPoint,
  ChartDataSlice,
  ChartSeries,
  ResolvedChartSeries,
} from '../ChartData'
import type { ChartSeriesOutput } from '../ChartSeries'
import { useChartWrapperContext } from '../ChartWrapper/ChartWrapper'
import { ChartLineConstants } from './ChartLine.constants'
import {
  type ChartLineCurveType,
  type ChartLineOptions,
  type ChartLineStyle,
  chartLineOptions,
} from './ChartLine.options'
import {
  ChartLineInputParts,
  ChartLineOutputParts,
  ChartLineUtils,
} from './ChartLine.parts'

export { chartLineOptions } from './ChartLine.options'
export const mapColorPalette = Colors.mapColorPalette
export type {
  ChartColorPalette,
  ChartLineCurveType,
  ChartLineOptions,
  ChartLineStyle,
} from './ChartLine.options'

export type ChartLineSeries = ChartSeries & {
  color?: string
  fillColor?: string
  highlightColor?: string
}

/** Pipeline input consumed by ChartLine.setup. */
export type ChartLineInput = ChartLineOptions & {
  colorPalette?: ChartColorPalette
  lineWidth?: number
  lineStyle?: ChartLineStyle
  _series?: Options.State[]
}

/**
 * Style options mapped to VisX-ready props in setup.
 * `curveType` stays serializable; Line binds `ChartLineConstants.curves[curveType]`.
 */
export type ResolvedChartLineSeries = ResolvedChartSeries & {
  visualization: 'line'
  color: string
  fillColor: string
  highlightColor: string
  fillArea: boolean
  fillOpacity: number
  lineWidth: number
  lineStyle: ChartLineStyle
  strokeDasharray: string | undefined
  curveType: ChartLineCurveType
  showDataPoints: boolean
  pointRadius: number
  pointStrokeWidth: number
  pointHighlightStrokeWidth: number
  pointGapMaskId: string
  /** Whether PointGapMask should be applied (points present + markers on). */
  usePointGapMask: boolean
}

export interface ChartLineRenderProps
  extends Pick<
      ResolvedChartLineSeries,
      | 'fillArea'
      | 'fillOpacity'
      | 'lineWidth'
      | 'lineStyle'
      | 'curveType'
      | 'showDataPoints'
      | 'pointRadius'
    >,
    Pick<ChartDataSlice, 'dimensionKey'> {}

const chartLineSetup = () =>
  RenderPipeline.createFeatureSetup<
    ChartLineInput,
    ChartSeriesOutput,
    ChartDataOutput & ChartSeriesOutput & Pick<QueryOutput, 'contentNode'>
  >(
    flow(
      ChartLineInputParts.setupInput(),
      ChartLineOutputParts.setLineSeries(),
    ),
  )

export interface ChartLineProps {
  series: ResolvedChartSeries
  data: ChartDataSlice
}

/** Dumb mark — binds precalculated series props to VisX within wrapper scales. */
const Line = ({ series, data }: ChartLineProps) => {
  // Unique per mount — Trigger + Expanded dual trees share seriesKey mask ids
  // otherwise, and url(#…) resolves to the hidden Trigger mask (line vanishes).
  const instanceId = useId().replace(/:/g, '')
  const { scales, innerWidth, innerHeight } = useChartWrapperContext()
  const { dimensionKey } = data

  if (scales == null || dimensionKey == null || series.dataKey == null) {
    return null
  }
  if (scales.orientation === 'horizontal') {
    return null
  }
  if (!ChartLineUtils.isResolvedLineSeries(series)) {
    return null
  }

  const maskId = `${series.pointGapMaskId}-${instanceId}`
  const { xScale, yScale } = scales
  const rangeStart =
    typeof yScale.range === 'function' ? yScale.range()[0] : undefined
  const y0 =
    typeof rangeStart === 'number' && Number.isFinite(rangeStart)
      ? rangeStart
      : 0
  const bandwidth =
    'bandwidth' in xScale && typeof xScale.bandwidth === 'function'
      ? xScale.bandwidth()
      : 0
  const x = (value: string) => {
    const start = xScale(value)
    return start == null ? undefined : start + bandwidth / 2
  }
  const y = (value: number) => yScale(value)

  return (
    <g>
      <ChartLineUtils.PointGapMask
        series={series}
        maskId={maskId}
        xScale={x}
        yScale={y}
        innerWidth={innerWidth}
        innerHeight={innerHeight}
      />
      <g mask={series.usePointGapMask ? `url(#${maskId})` : undefined}>
        <ChartLineUtils.Area
          series={series}
          xScale={x}
          yScale={yScale}
          y0={y0}
        />
        <LinePath<ChartDataPoint>
          data={series.points}
          x={point => ChartLineUtils.getPointX(point, x)}
          y={point => ChartLineUtils.getPointY(point, y)}
          defined={ChartLineConstants.isPointDefined}
          curve={ChartLineConstants.curves[series.curveType]}
          stroke={series.color}
          strokeWidth={series.lineWidth}
          strokeDasharray={series.strokeDasharray}
          fill="transparent"
        />
      </g>
      <ChartLineUtils.Points series={series} xScale={x} yScale={y} />
    </g>
  )
}

export const ChartLine = {
  options: chartLineOptions,
  Line,
  setup: chartLineSetup,
}
